import { Controller } from "@hotwired/stimulus";

// Styled file drop zone: drag/drop, click to select, file preview, enables submit when file selected
export default class extends Controller {
  static targets = ["zone", "input", "prompt", "preview", "fileName", "fileSize", "submitBtn"];
  static values = {
    maxSize: { type: Number, default: 50 },
  };

  connect() {
    this.zone?.addEventListener("dragover", this.handleDragOver.bind(this));
    this.zone?.addEventListener("dragleave", this.handleDragLeave.bind(this));
    this.zone?.addEventListener("drop", this.handleDrop.bind(this));
    this.zone?.addEventListener("click", (e) => {
      if (e.target.closest("button[type='button']")) return;
      this.input?.click();
    });
    this.zone?.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.input?.click();
      }
    });
    this.syncSubmitState();
  }

  disconnect() {
    this.zone?.removeEventListener("dragover", this.handleDragOver);
    this.zone?.removeEventListener("dragleave", this.handleDragLeave);
    this.zone?.removeEventListener("drop", this.handleDrop);
  }

  onFileSelect() {
    this.updatePreview();
    this.dispatch("change");
  }

  clearFile(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (this.hasInputTarget) this.inputTarget.value = "";
    this.updatePreview();
    this.dispatch("change");
  }

  updatePreview() {
    const file = this.input?.files?.[0];
    if (file) {
      this.promptTarget?.classList.add("hidden");
      this.previewTarget?.classList.remove("hidden");
      this.fileNameTarget.textContent = file.name;
      this.fileSizeTarget.textContent = this.formatSize(file.size);
    } else {
      this.promptTarget?.classList.remove("hidden");
      this.previewTarget?.classList.add("hidden");
    }
    this.syncSubmitState();
  }

  syncSubmitState() {
    const hasFile = this.input?.files?.length > 0;
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = !hasFile;
    }
  }

  formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    this.zone?.classList.add("border-blue-400", "bg-blue-50");
    this.zone?.style.setProperty("border-color", "#93c5fd");
    this.zone?.style.setProperty("background-color", "#eff6ff");
  }

  handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    this.zone?.classList.remove("border-blue-400", "bg-blue-50");
    this.zone?.style.removeProperty("border-color");
    this.zone?.style.removeProperty("background-color");
  }

  handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    this.handleDragLeave(e);
    const files = e.dataTransfer?.files;
    if (files?.length && this.input) {
      this.input.files = files;
      this.onFileSelect();
    }
  }

  dispatch(eventName) {
    this.element.dispatchEvent(new CustomEvent(`upload:${eventName}`, { bubbles: true }));
  }
}
